import { Fragment, useEffect, useMemo, useRef } from "react";
import { Button, CircularProgress, IconButton, Tooltip } from "@mui/material";
import {
  FaArrowLeft,
  FaCheck,
  FaUndoAlt,
  FaEllipsisH,
  FaStar,
  FaRegStar,
  FaRegClock,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toDate, fromNow } from "helpers/dateHelper";
import { useGetTicketMessages } from "hooks/useGetTicketMessages";
import { useGetTicketEvents } from "hooks/useGetTicketEvents";
import { Ticket } from "interfaces/entities/ticket.model";
import { queryClient } from "queryClient";
import { TicketReplyForm } from "../components/TicketReplyForm";
import { ActivityTimeline } from "./ActivityTimeline";
import { Avatar, DesignStatus, StatusPill, PriorityPill, DesignPriority } from "./primitives";

export type ConvTab = "conversation" | "internal" | "activity";

interface ConversationColumnProps {
  ticket: Ticket;
  status: string;
  priority: DesignPriority;
  starred: boolean;
  onToggleStar: () => void;
  onMarkResolved: () => void;
  tab: ConvTab;
  onTabChange: (t: ConvTab) => void;
}

const isStaffMessage = (m: any) => {
  const sender = (m?.senderType || "").toString().toLowerCase();
  if (sender) {
    return ["member", "agent", "staff", "assistant"].includes(sender);
  }
  return Boolean(m?.memberID);
};

const getSenderName = (m: any): string => {
  if (isStaffMessage(m)) return m?.member?.name || "Сотрудник";
  return [m?.customer?.lastName, m?.customer?.firstName].filter(Boolean).join(" ") || "Клиент";
};

// Внутренняя заметка — TicketMessage с `isInternal=true`. Это «канонический»
// флаг с бэка; `internal` / `kind` / `type` оставлены для совместимости
// с возможными альтернативными формами полей в payload.
const isInternalNote = (m: any) =>
  Boolean(m?.isInternal) ||
  Boolean(m?.internal) ||
  m?.kind === "internal" ||
  m?.type === "internal";

const messageKind = (m: any): "customer" | "agent" | "internal" => {
  if (isInternalNote(m)) return "internal";
  return isStaffMessage(m) ? "agent" : "customer";
};

const dayLabel = (d: Date) => {
  // Returns RU-localised day label ("Сегодня", "Вчера", или дата).
  const now = new Date();
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((today.getTime() - start.getTime()) / 86400000);
  if (diffDays === 0) return "Сегодня";
  if (diffDays === 1) return "Вчера";
  return d.toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
};

export const ConversationColumn = ({
  ticket,
  status,
  priority,
  starred,
  onToggleStar,
  onMarkResolved,
  tab,
  onTabChange,
}: ConversationColumnProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { data: rawMessages = [], isLoading: isMessagesLoading } = useGetTicketMessages(ticket.id);
  const { data: rawEvents = [] } = useGetTicketEvents(String(ticket.id));
  const messagesRef = useRef<HTMLDivElement>(null);

  const messages = useMemo(() => {
    return (rawMessages as any[]).map((m) => ({
      id: m.id,
      kind: messageKind(m),
      authorName: getSenderName(m),
      content: m.content || m.text || "",
      createdAt: m.createdAt ? new Date(m.createdAt) : new Date(),
      attachment:
        m.message?.image && m.message?.mimeType
          ? {
              url: m.message.image,
              mime: m.message.mimeType as string,
              name: m.message?.filename || "attachment",
            }
          : null,
    }));
  }, [rawMessages]);

  const counts = useMemo(
    () => ({
      conversation: messages.filter((m) => m.kind !== "internal").length,
      internal: messages.filter((m) => m.kind === "internal").length,
      activity: (rawEvents as any[]).length,
    }),
    [messages, rawEvents],
  );

  // Day-grouped, filtered view for the messages list.
  const visible = useMemo(() => {
    if (tab === "conversation") return messages.filter((m) => m.kind !== "internal");
    if (tab === "internal") return messages.filter((m) => m.kind === "internal");
    return [];
  }, [messages, tab]);

  const rendered = useMemo(() => {
    const out: Array<
      { type: "day"; id: string; label: string } | { type: "msg"; msg: (typeof visible)[number] }
    > = [];
    let lastDay: string | null = null;
    for (const m of visible) {
      const label = dayLabel(m.createdAt);
      if (label !== lastDay) {
        out.push({ type: "day", id: `d-${label}-${m.id}`, label });
        lastDay = label;
      }
      out.push({ type: "msg", msg: m });
    }
    return out;
  }, [visible]);

  // Auto-scroll to bottom when the message list grows.
  //   useEffect(() => {
  //     const el = messagesRef.current;
  //     if (el) el.scrollTop = el.scrollHeight;
  //   }, [visible.length]);

  const firstMessage = messages[0];
  const isResolved = status === "CLOSED";

  return (
    <section className="tv2-conv">
      {/* Header */}
      <div className="tv2-conv-header">
        <div className="tv2-conv-header-left">
          <div className="tv2-ticket-title-row">
            <Tooltip title="К списку обращений">
              <IconButton
                size="small"
                onClick={() => navigate("/admin/tickets")}
                aria-label="К списку обращений"
              >
                <FaArrowLeft size={13} />
              </IconButton>
            </Tooltip>
            <span className="tv2-ticket-id">#{ticket.id}</span>
            <h1 className="tv2-ticket-title">{ticket.subject || "Без темы"}</h1>
            <StatusPill status={status} />
            <PriorityPill priority={priority} />
          </div>
        </div>
        <div className="tv2-conv-header-right">
          <Tooltip title={starred ? "Снять отметку" : "Отметить"}>
            <IconButton
              size="small"
              onClick={onToggleStar}
              sx={{ color: starred ? "var(--tv2-amber)" : "var(--tv2-text-2)" }}
              aria-label={starred ? "Снять отметку" : "Отметить"}
            >
              {starred ? <FaStar size={14} /> : <FaRegStar size={14} />}
            </IconButton>
          </Tooltip>
          <Button
            size="small"
            variant={isResolved ? "outlined" : "contained"}
            color={isResolved ? "success" : "primary"}
            onClick={onMarkResolved}
            startIcon={isResolved ? <FaUndoAlt size={11} /> : <FaCheck size={11} />}
          >
            {isResolved ? t("ticket.reopen") : "Закрыть"}
          </Button>
          <Tooltip title="Ещё">
            <IconButton size="small" aria-label="Ещё">
              <FaEllipsisH size={14} />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Tabs */}
      <div className="tv2-tabs" role="tablist">
        <button
          role="tab"
          aria-selected={tab === "conversation"}
          className={`tv2-tab${tab === "conversation" ? " active" : ""}`}
          onClick={() => onTabChange("conversation")}
        >
          Переписка
          <span className="tv2-tab-count">{counts.conversation}</span>
        </button>
        <button
          role="tab"
          aria-selected={tab === "internal"}
          className={`tv2-tab${tab === "internal" ? " active internal" : ""}`}
          onClick={() => onTabChange("internal")}
        >
          Заметки
          <span className="tv2-tab-count">{counts.internal}</span>
        </button>
        <button
          role="tab"
          aria-selected={tab === "activity"}
          className={`tv2-tab${tab === "activity" ? " active" : ""}`}
          onClick={() => onTabChange("activity")}
        >
          Действия
          <span className="tv2-tab-count">{counts.activity}</span>
        </button>
        {firstMessage && (
          <div className="tv2-tabs-meta">
            <FaRegClock size={11} />
            Открыт {fromNow(firstMessage.createdAt)}
          </div>
        )}
      </div>

      {/* Messages / Activity */}
      <div className="tv2-messages" ref={messagesRef} role="tabpanel" aria-label={tab}>
        {tab !== "activity" ? (
          <>
            {/* Original ticket content + ticket.data custom fields.
                            Mirrors the v1 "Обращение" card. Only shown in the
                            main conversation tab. */}
            {tab === "conversation" && <OriginalRequestCard ticket={ticket} />}
            {isMessagesLoading ? (
              <div className="tv2-status">
                <CircularProgress size={20} />
              </div>
            ) : rendered.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "40px 20px",
                  color: "var(--tv2-text-3)",
                  fontSize: 13,
                }}
              >
                {tab === "internal" ? "Заметок пока нет." : "Сообщений пока нет."}
              </div>
            ) : (
              rendered.map((g) =>
                g.type === "day" ? (
                  <div key={g.id} className="tv2-day-divider">
                    <div className="tv2-day-divider-line" />
                    <span className="tv2-day-divider-label">{g.label}</span>
                    <div className="tv2-day-divider-line" />
                  </div>
                ) : (
                  <MessageBubble key={g.msg.id} msg={g.msg} />
                ),
              )
            )}
          </>
        ) : (
          // Action feed renders as a proper vertical timeline with
          // a rail, coloured dots per event type, and from→to chips.
          <ActivityTimeline ticketID={ticket.id} />
        )}
      </div>

      {/* Reply form (hidden on the activity tab). We render the existing
                TicketReplyForm so operators keep voice input, templates, AI
                rewrite, translate, and audio attachments. */}
      {tab !== "activity" && (
        <div className="tv2-composer-wrap">
          <div className="tv2-composer" style={{ padding: 8 }}>
            <TicketReplyForm
              ticketID={ticket.id}
              ticket={ticket}
              isInternal={tab === "internal"}
              onSent={() => {
                queryClient.invalidateQueries({
                  queryKey: ["tickets", ticket.id, "messages"],
                });
                queryClient.invalidateQueries({
                  queryKey: ["events", "ticket", ticket.id],
                });
              }}
            />
          </div>
        </div>
      )}
    </section>
  );
};

// Mirrors the v1 "Обращение" Paper at the top of the conversation: the
// customer's original ticket content + any custom fields stored in
// ticket.data (mapped via the `variables.<key>` i18n keys).
const OriginalRequestCard = ({ ticket }: { ticket: Ticket }) => {
  const { t } = useTranslation();
  const customFields = Object.entries((ticket.data as any) || {}).filter(
    ([key]) => key !== "content",
  );
  if (!ticket.content && customFields.length === 0) return null;
  return (
    <div
      style={{
        background: "var(--tv2-surface)",
        border: "1px solid var(--tv2-border)",
        borderRadius: "var(--tv2-radius)",
        padding: "12px 14px",
        marginBottom: 18,
        boxShadow: "var(--tv2-shadow-sm)",
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: "var(--tv2-text-3)",
          marginBottom: 8,
        }}
      >
        Обращение
      </div>
      {ticket.content && (
        <div
          style={{
            fontSize: 14,
            whiteSpace: "pre-wrap",
            lineHeight: 1.55,
            color: "var(--tv2-text)",
          }}
        >
          {ticket.content}
        </div>
      )}
      {customFields.length > 0 && (
        <div
          style={{
            marginTop: ticket.content ? 12 : 0,
            paddingTop: ticket.content ? 10 : 0,
            borderTop: ticket.content ? "1px solid var(--tv2-border)" : "none",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          {customFields.map(([key, value]: [string, any]) => (
            <div
              key={key}
              style={{
                display: "flex",
                gap: 8,
                fontSize: 12.5,
                alignItems: "baseline",
              }}
            >
              <span style={{ color: "var(--tv2-text-3)", minWidth: 120 }}>
                {t(`variables.${key}`, { defaultValue: key })}:
              </span>
              <span style={{ color: "var(--tv2-text)" }}>
                {key === "priorityClassifier" ? String(value?.reasoning ?? value) : String(value)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

interface MessageBubbleProps {
  msg: {
    id: number | string;
    kind: "customer" | "agent" | "internal";
    authorName: string;
    content: string;
    createdAt: Date;
    attachment: { url: string; mime: string; name: string } | null;
  };
}

const roleLabel: Record<MessageBubbleProps["msg"]["kind"], string> = {
  customer: "Клиент",
  agent: "Сотрудник",
  internal: "Внутренняя заметка",
};

const formatContent = (text: string) => {
  // Highlight @mentions in indigo and inline `code` per the design.
  return text.split("\n").map((line, lineIdx, lines) => {
    const parts: Array<string | JSX.Element> = [];
    const regex = /(@\w+|`[^`]+`)/g;
    let lastIdx = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(line)) !== null) {
      if (match.index > lastIdx) parts.push(line.slice(lastIdx, match.index));
      if (match[0].startsWith("@")) {
        parts.push(
          <span
            key={`${lineIdx}-${match.index}`}
            style={{ color: "var(--tv2-accent-text)", fontWeight: 600 }}
          >
            {match[0]}
          </span>,
        );
      } else {
        parts.push(<code key={`${lineIdx}-${match.index}`}>{match[0].slice(1, -1)}</code>);
      }
      lastIdx = match.index + match[0].length;
    }
    if (lastIdx < line.length) parts.push(line.slice(lastIdx));
    return (
      <Fragment key={lineIdx}>
        {parts}
        {lineIdx < lines.length - 1 && <br />}
      </Fragment>
    );
  });
};

const MessageAttachment = ({
  attachment,
}: {
  attachment: { url: string; mime: string; name: string };
}) => {
  if (attachment.mime.startsWith("image/")) {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" style={{ display: "block" }}>
        <img
          src={attachment.url}
          alt={attachment.name}
          style={{
            maxWidth: 280,
            maxHeight: 240,
            borderRadius: 8,
            display: "block",
            objectFit: "cover",
          }}
        />
      </a>
    );
  }
  if (attachment.mime.startsWith("video/")) {
    return (
      <video
        src={attachment.url}
        controls
        style={{ maxWidth: 320, maxHeight: 240, borderRadius: 8, display: "block" }}
      />
    );
  }
  if (attachment.mime.startsWith("audio/")) {
    return <audio src={attachment.url} controls style={{ maxWidth: 320, display: "block" }} />;
  }
  return (
    <a href={attachment.url} target="_blank" rel="noreferrer" className="tv2-attachment-chip">
      <span className="tv2-attachment-icon" style={{ background: "#6366F1" }}>
        {(attachment.name.split(".").pop() || "FILE").toUpperCase().slice(0, 3)}
      </span>
      <span className="tv2-attachment-name">{attachment.name}</span>
    </a>
  );
};

const MessageBubble = ({ msg }: MessageBubbleProps) => {
  const bodyCls = msg.kind === "agent" ? "agent" : msg.kind === "internal" ? "internal" : "";
  const roleCls = bodyCls;
  return (
    <div className="tv2-message">
      <Avatar name={msg.authorName} size="lg" seed={msg.authorName + msg.kind} />
      <div className={`tv2-message-body ${bodyCls}`}>
        <div className="tv2-message-meta">
          <span className="tv2-message-author">{msg.authorName}</span>
          <span className={`tv2-message-role ${roleCls}`}>{roleLabel[msg.kind]}</span>
          <span className="tv2-message-time">{toDate(msg.createdAt)}</span>
        </div>
        {msg.attachment && (
          <div className="tv2-message-attachments" style={{ marginBottom: 8 }}>
            <MessageAttachment attachment={msg.attachment} />
          </div>
        )}
        <div className="tv2-message-content">{formatContent(msg.content)}</div>
      </div>
    </div>
  );
};
